// widgetModel.js
// The View widget model: what a dashboard card is and how it is stored.
//
// One card is one widget: it may render a function instance's whole composite
// result ('tabs'), or exactly one manifest entry of it — a data table, a single
// aggregated number, a chart, a file, or a folder shelf — or no data at all
// ('header').
//
// Stored on `users/{cid}/dashboard_views/{view_id}`:
//
//   widgets: { [widgetId]: {
//       instanceId: string | null,                       // key into functionInstanceMap; null only for 'header'
//       binding: { source_block_id, entry_key } | null,  // null => the whole composite
//       display: 'tabs'|'table'|'stat'|'chart'|'file'|'shelf'|'header',
//       config: object,                                  // per display; see STAT config below
//       title: string | null
//   } }
//   layouts: react-grid-layout items keyed by widgetId

import { isDiffLeaf } from './diffLeaves';
import { countNoun } from './pluralise';

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

// --- Displays ---------------------------------------------------------------

export const WIDGET_DISPLAYS = ['tabs', 'table', 'stat', 'chart', 'file', 'shelf', 'header'];

export const DISPLAY_LABELS = {
  tabs: 'All tabs',
  table: 'Table',
  stat: 'Stat tile',
  chart: 'Chart',
  file: 'File',
  shelf: 'Folder shelf',
  header: 'Header',
};

// Minimum grid footprint, in the 16-column grid. Below these a widget cannot
// render its own chrome, so react-grid-layout is told to refuse the resize.
export const DISPLAY_MIN_SIZE = {
  stat: { w: 3, h: 2 },
  chart: { w: 5, h: 5 },
  table: { w: 8, h: 5 },
  file: { w: 6, h: 5 },
  shelf: { w: 6, h: 4 },
  tabs: { w: 8, h: 6 },
  header: { w: 4, h: 1 },
};

// The footprint a freshly dropped widget takes.
export const DISPLAY_DEFAULT_SIZE = {
  stat: { w: 4, h: 3 },
  chart: { w: 8, h: 6 },
  table: { w: 16, h: 7 },
  file: { w: 8, h: 6 },
  shelf: { w: 8, h: 5 },
  tabs: { w: 12, h: 7 },
  header: { w: 16, h: 1 },
};

// Dropping a manifest entry picks its display automatically from the entry kind.
export const KIND_DEFAULT_DISPLAY = {
  data: 'table',
  chart: 'chart',
  file: 'file',
  folder: 'shelf',
};

// Which displays a given entry kind can legitimately take. Only `data` has a
// genuine choice; a chart is a chart and a file is a file.
export const KIND_ALLOWED_DISPLAYS = {
  data: ['table', 'stat'],
  chart: ['chart'],
  file: ['file'],
  folder: ['shelf'],
};

// The grid geometry. Both the builder and the live view use these.
export const GRID_COLS = 16;
export const GRID_ROW_HEIGHT = 48;
export const GRID_MARGIN = 10;

export const displayMinSize = (display) => DISPLAY_MIN_SIZE[display] || DISPLAY_MIN_SIZE.tabs;
export const displayDefaultSize = (display) => DISPLAY_DEFAULT_SIZE[display] || DISPLAY_DEFAULT_SIZE.tabs;

// --- Widgets ----------------------------------------------------------------

export const isBinding = (value) => (
  isPlainObject(value)
  && typeof value.source_block_id === 'string'
  && typeof value.entry_key === 'string'
);

export const bindingKey = (binding) => (
  isBinding(binding) ? `${binding.source_block_id}::${binding.entry_key}` : ''
);

export const sameBinding = (a, b) => bindingKey(a) === bindingKey(b) && bindingKey(a) !== '';

export const normalizeWidget = (raw, fallbackInstanceId = null) => {
  const display = WIDGET_DISPLAYS.includes(raw?.display) ? raw.display : 'tabs';
  const instanceId = typeof raw?.instanceId === 'string' && raw.instanceId
    ? raw.instanceId
    : (display === 'header' ? null : fallbackInstanceId);
  return {
    instanceId: instanceId || null,
    binding: isBinding(raw?.binding)
      ? { source_block_id: raw.binding.source_block_id, entry_key: raw.binding.entry_key }
      : null,
    display,
    config: isPlainObject(raw?.config) ? { ...raw.config } : {},
    title: typeof raw?.title === 'string' && raw.title.trim() !== '' ? raw.title : null,
  };
};

export const makeWidgetId = () => `w_${Date.now()}_${Math.floor(Math.random() * 100000)}`;

export const makeInstanceId = (functionId) => (
  `${functionId}_instance_${Date.now()}_${Math.floor(Math.random() * 1000)}`
);

// Ensure a layout item carries the minimum footprint its display requires and
// never a smaller w/h than that minimum.
export const withDisplayConstraints = (item, display) => {
  const min = displayMinSize(display);
  return {
    i: item.i,
    x: Number.isFinite(item.x) ? item.x : 0,
    y: Number.isFinite(item.y) ? item.y : 0,
    w: Math.max(Number.isFinite(item.w) ? item.w : min.w, min.w),
    h: Math.max(Number.isFinite(item.h) ? item.h : min.h, min.h),
    minW: min.w,
    minH: min.h,
  };
};

// Place a new layout item after every existing item before applying the normal
// numeric and display-size constraints. This keeps append placement separate
// from generic coordinate sanitisation.
export const appendLayoutItem = (layouts, item, display) => {
  const bottom = layouts.reduce(
    (currentBottom, layout) => Math.max(currentBottom, layout.y + layout.h),
    0,
  );
  return withDisplayConstraints({ ...item, y: bottom }, display);
};

// Read a view document into the widget model: { widgets, layouts }. Nothing
// here writes.
export function resolveViewModel(viewData) {
  const rawLayouts = Array.isArray(viewData?.layouts) ? viewData.layouts : [];
  const rawWidgets = isPlainObject(viewData?.widgets) ? viewData.widgets : {};

  const widgets = {};
  Object.entries(rawWidgets).forEach(([widgetId, raw]) => {
    widgets[widgetId] = normalizeWidget(raw);
  });
  const layouts = rawLayouts
    .filter((item) => item && widgets[item.i])
    .map((item) => withDisplayConstraints(item, widgets[item.i].display));
  // A widget with no layout entry would be invisible; give it one at the end.
  Object.keys(widgets).forEach((widgetId) => {
    if (layouts.some((item) => item.i === widgetId)) return;
    const size = displayDefaultSize(widgets[widgetId].display);
    layouts.push(appendLayoutItem(
      layouts,
      { i: widgetId, x: 0, ...size },
      widgets[widgetId].display,
    ));
  });
  return { widgets, layouts };
}

// The distinct function instances a widget map references, in first-seen order.
export const widgetInstanceIds = (widgets) => {
  const seen = [];
  Object.values(widgets || {}).forEach((widget) => {
    if (!widget?.instanceId) return;
    if (!seen.includes(widget.instanceId)) seen.push(widget.instanceId);
  });
  return seen;
};

// Only saved View content participates in change notices. Lease heartbeats,
// audit timestamps and field ordering must not interrupt an open View.
export function viewContentSignature(view) {
  if (!view) return null;
  const content = {};
  for (const field of ['name', 'description', 'widgets', 'layouts', 'functionIds', 'functionInstanceMap']) {
    content[field] = view[field] ?? null;
  }
  const ordered = (value) => {
    if (Array.isArray(value)) return value.map(ordered);
    if (!isPlainObject(value)) return value;
    return Object.fromEntries(Object.keys(value).sort().map((key) => [key, ordered(value[key])]));
  };
  return JSON.stringify(ordered(JSON.parse(JSON.stringify(content))));
}

// A widget that shows rows needs the full result from storage when the run's
// bounded preview is truncated; a chart / file / folder / header never does.
export const displayConsumesRows = (display) => display === 'tabs' || display === 'table' || display === 'stat';

// --- Composite entries ------------------------------------------------------

// A composite result is { outputs: { [output_block_id]: { output_type, tabs, skipped } } }
// and every produced tab carries `kind`, `key` and `source_block_id`. The widget
// binding identity is (source_block_id, entry_key) — the same identity chart
// settings already use — so it survives the Output block being re-bound.

export const ENTRY_KINDS = ['data', 'chart', 'file', 'folder'];

// An entry key is a machine identifier — `premium_summary`, `reports/` — and
// showing it raw is how a produced table ends up labelled "data" on screen. A
// real filename is already human-readable and must survive intact: "Q3
// Report.Xlsx" is a worse label than "Q3 report.xlsx", so anything ending in an
// extension is returned untouched.
const FILENAME_RE = /\.[A-Za-z0-9]{1,8}$/;

export const prettifyEntryKey = (key) => {
  const raw = String(key ?? '').trim();
  if (raw === '') return '';
  if (FILENAME_RE.test(raw)) return raw;
  const words = raw.replace(/\/+$/, '').replace(/_/g, ' ').trim();
  return words.replace(/\b\w/g, (character) => character.toUpperCase());
};

export const entryLabel = (entry) => {
  if (!entry) return '';
  if (entry.kind === 'chart') return entry.chart?.title || prettifyEntryKey(entry.key);
  if (entry.kind === 'file') return entry.filename || prettifyEntryKey(entry.key);
  return prettifyEntryKey(entry.key) || entry.kind || '';
};

const formatBytes = (value) => {
  if (!Number.isFinite(value)) return null;
  if (value < 1024) return `${value} B`;
  if (value < 1024 ** 2) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 ** 2)).toFixed(1)} MB`;
};

export const entryMeta = (entry) => {
  if (!entry) return '';
  if (entry.kind === 'data') {
    const rows = Array.isArray(entry.rows) ? entry.rows.length : 0;
    const total = Number.isFinite(entry.total_rows) ? entry.total_rows : rows;
    const columns = rows > 0 ? Object.keys(entry.rows[0] || {}).length : 0;
    return `${countNoun(total, 'row')} · ${countNoun(columns, 'col')}`;
  }
  if (entry.kind === 'chart') return entry.chart?.type ? `${entry.chart.type} chart` : 'chart';
  if (entry.kind === 'file') return [entry.mime, formatBytes(entry.size_bytes)].filter(Boolean).join(' · ') || 'file';
  if (entry.kind === 'folder') return `${(entry.files || []).length} files`;
  return entry.kind || '';
};

// Every produced entry of a composite result, in output-block then tab order,
// deduplicated on the binding identity.
export function compositeEntries(outputs) {
  const outputsMap = isPlainObject(outputs) ? outputs : {};
  const entries = [];
  const seen = new Set();
  Object.entries(outputsMap).forEach(([outputBlockId, output]) => {
    if (!Array.isArray(output?.tabs)) return;
    output.tabs.forEach((tab) => {
      if (!tab || !ENTRY_KINDS.includes(tab.kind)) return;
      const binding = { source_block_id: tab.source_block_id, entry_key: tab.key };
      const key = bindingKey(binding);
      if (!key || seen.has(key)) return;
      seen.add(key);
      entries.push({
        ...tab,
        outputBlockId,
        binding,
        label: entryLabel(tab),
        meta: entryMeta(tab),
      });
    });
  });
  return entries;
}

// Resolve a widget binding against a composite result. Returns null when this
// run did not produce that entry (a conditional artifact, or a run of an older
// version) — the widget then says so rather than rendering empty.
export function findCompositeEntry(outputs, binding) {
  if (!isBinding(binding)) return null;
  const outputsMap = isPlainObject(outputs) ? outputs : {};
  let found = null;
  Object.entries(outputsMap).some(([outputBlockId, output]) => {
    if (!Array.isArray(output?.tabs)) return false;
    const match = output.tabs.find((tab) => (
      tab
      && tab.source_block_id === binding.source_block_id
      && tab.key === binding.entry_key
    ));
    if (match) {
      found = { ...match, outputBlockId };
      return true;
    }
    return false;
  });
  return found;
}

export const isEntryTruncated = (entry) => (
  entry?.kind === 'data'
  && Number.isFinite(entry.total_rows)
  && entry.total_rows > (Array.isArray(entry.rows) ? entry.rows.length : 0)
);

// --- Stat tiles -------------------------------------------------------------

// A stat tile is one aggregation over one column of a data entry's rows — the
// spreadsheet SUM of a column, nothing more. Anything conditional belongs in a
// Logic block.

export const STAT_AGGREGATIONS = [
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'count', label: 'Count rows' },
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
  { value: 'first', label: 'First value' },
];

export const STAT_FORMATS = [
  { value: 'gbp', label: '£ currency' },
  { value: 'num', label: 'Plain number' },
  { value: 'pct', label: 'Percent' },
];

export const DEFAULT_STAT_CONFIG = { column: '', agg: 'sum', label: '', format: 'num' };

const toNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[£$€,\s]/g, '');
    if (cleaned === '') return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

// A time-series result carries each period as one object-valued column
// (`'2026_03': { Gross_Premium: 10520.55, … }`), so a tile aggregates LEAVES:
// a column is a dotted path into the row, and a level of nesting is any plain
// object that is not a tagged diff leaf (a diff leaf is a value of its own).
const isNestedValue = (value) => isPlainObject(value) && !isDiffLeaf(value);

// The value at a column path. A real top-level column whose name contains a
// dot still resolves as itself before the path is walked.
export const readColumn = (row, column) => {
  if (!isPlainObject(row) || !column) return null;
  if (Object.prototype.hasOwnProperty.call(row, column)) return row[column];
  let value = row;
  for (const part of String(column).split('.')) {
    if (!isNestedValue(value) || !Object.prototype.hasOwnProperty.call(value, part)) return null;
    value = value[part];
  }
  return value;
};

// Every leaf column path observed across the rows, in first-seen order.
export const rowColumns = (rows) => {
  const columns = [];
  const collect = (value, prefix) => {
    Object.keys(value).forEach((key) => {
      const path = prefix ? `${prefix}.${key}` : key;
      if (isNestedValue(value[key])) {
        collect(value[key], path);
      } else if (!columns.includes(path)) {
        columns.push(path);
      }
    });
  };
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (isPlainObject(row)) collect(row, '');
  });
  return columns;
};

// Columns whose values parse as numbers in at least one row. `count` works on
// any column, so the editor still offers every column — this is only used to
// order the list sensibly.
export const numericColumns = (rows) => rowColumns(rows).filter((column) => (
  (Array.isArray(rows) ? rows : []).some((row) => toNumber(readColumn(row, column)) !== null)
));

export function aggregateRows(rows, column, agg) {
  const list = Array.isArray(rows) ? rows : [];
  if (agg === 'count') return list.length;
  if (!column) return null;
  if (agg === 'first') {
    const first = list.map((row) => readColumn(row, column)).find((value) => value !== undefined && value !== null);
    if (first === undefined) return null;
    const numeric = toNumber(first);
    return numeric === null ? first : numeric;
  }
  const values = list.map((row) => toNumber(readColumn(row, column))).filter((value) => value !== null);
  if (values.length === 0) return null;
  if (agg === 'sum') return values.reduce((total, value) => total + value, 0);
  if (agg === 'avg') return values.reduce((total, value) => total + value, 0) / values.length;
  if (agg === 'min') return Math.min(...values);
  if (agg === 'max') return Math.max(...values);
  return null;
}

// The tile is the ONLY readout of its number, so it shows the exact value:
// pounds to the penny, percentages and plain numbers with the decimals they
// have (a magnitude-rounded "£100" for £100.49 is a different number).
export function formatStatValue(value, format) {
  if (value === null || value === undefined) return '—';
  if (typeof value !== 'number') return String(value);
  const sign = value < 0 ? '-' : '';
  const size = Math.abs(value);
  if (format === 'gbp') {
    return `${sign}£${size.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (format === 'pct') {
    return `${sign}${size.toLocaleString('en-GB', { maximumFractionDigits: 2 })}%`;
  }
  return `${sign}${size.toLocaleString('en-GB', { maximumFractionDigits: 6 })}`;
}
